import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, ShieldCheck, XCircle, CheckCircle, Notebook, ClockClockwise } from "@phosphor-icons/react";

function ProductionLogPanel({ orderId }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ sources_used: "", candidate_volume: "", duplicates_removed: "", exclusions_applied: "", note: "" });
  const load = async () => { const { data } = await api.get(`/orders/${orderId}/production-logs`); setItems(data.items); };
  useEffect(() => { load(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => {
    if (!form.note && !form.sources_used) { toast.error("Add sources or a note"); return; }
    await api.post(`/orders/${orderId}/production-logs`, {
      ...form,
      candidate_volume: form.candidate_volume ? Number(form.candidate_volume) : null,
      duplicates_removed: form.duplicates_removed ? Number(form.duplicates_removed) : null,
    });
    toast.success("Log added");
    setForm({ sources_used: "", candidate_volume: "", duplicates_removed: "", exclusions_applied: "", note: "" });
    load();
  };
  return (
    <div className="border border-border bg-white p-5">
      <div className="font-semibold mb-3 flex items-center gap-2"><Notebook size={16} weight="duotone" className="text-primary" /> Production log (internal only)</div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div><Label className="text-xs">Sources used</Label><Input value={form.sources_used} onChange={(e) => setForm({ ...form, sources_used: e.target.value })} className="mt-1 h-9" placeholder="Never shown to customer" data-testid="prod-sources" /></div>
        <div><Label className="text-xs">Candidate volume</Label><Input type="number" value={form.candidate_volume} onChange={(e) => setForm({ ...form, candidate_volume: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Duplicates removed</Label><Input type="number" value={form.duplicates_removed} onChange={(e) => setForm({ ...form, duplicates_removed: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Exclusions applied</Label><Input value={form.exclusions_applied} onChange={(e) => setForm({ ...form, exclusions_applied: e.target.value })} className="mt-1 h-9" /></div>
      </div>
      <div className="mt-3"><Label className="text-xs">Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
      <Button data-testid="prod-add" onClick={add} className="rounded-sm mt-3">Add entry</Button>
      <div className="mt-4 space-y-2">
        {items.map((l) => (
          <div key={l.log_id} className="border-l-2 border-primary pl-3 py-1 text-xs">
            <div className="text-muted-foreground font-mono">{new Date(l.created_at).toLocaleString()}</div>
            {l.sources_used && <div><b>Sources:</b> {l.sources_used}</div>}
            {l.candidate_volume != null && <div><b>Volume:</b> {l.candidate_volume}</div>}
            {l.duplicates_removed != null && <div><b>Duplicates removed:</b> {l.duplicates_removed}</div>}
            {l.exclusions_applied && <div><b>Exclusions:</b> {l.exclusions_applied}</div>}
            {l.note && <div>{l.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function FileHistoryPanel({ orderId }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/orders/${orderId}/file-history`).then((r) => setItems(r.data.items || [])); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (items.length === 0) return null;
  return (
    <div className="border border-border bg-white p-5">
      <div className="font-semibold mb-3 flex items-center gap-2"><ClockClockwise size={16} weight="duotone" className="text-primary" /> File history</div>
      <div className="space-y-1 text-sm">
        {items.map((f) => (
          <div key={f.file_id} className="flex justify-between border-b border-border py-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-mono uppercase text-muted-foreground">{f.kind}·v{f.version}</span>
              <span className="font-medium">{f.original_filename}</span>
              {f.revoked && <span className="text-destructive text-[10px]">REVOKED</span>}
            </div>
            <div className="text-muted-foreground">{new Date(f.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderRow({ o }) {
  return (
    <Link to={`/admin/orders/${o.order_id}`} className="block border-b border-border p-4 hover:bg-muted">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="font-mono text-xs text-muted-foreground">{o.reference}</span>
            <StatusPill status={o.status} />
          </div>
          <div className="text-sm font-semibold">{o.currency} {o.amount?.toLocaleString()} · {o.option_key}</div>
        </div>
        <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</div>
      </div>
    </Link>
  );
}

export function AdminOrdersList() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data.items || [])); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Orders</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl mb-6">All orders.</h1>
        <div className="border border-border bg-white">
          {orders.map((o) => <OrderRow key={o.order_id} o={o} />)}
          {orders.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">No orders yet.</div>}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function AdminOrderDetail() {
  const id = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [qa, setQa] = useState({
    total_received: 0, total_reviewed: 0, duplicates_removed: 0,
    rejected_title_mismatch: 0, rejected_company_mismatch: 0, rejected_geo_mismatch: 0,
    emails_checked: 0, valid_emails: 0, invalid_emails: 0, risky_emails: 0,
    phones_checked: 0, suppression_applied: false, account_caps_applied: false,
    final_count: 0, comments: "", decision: "approved",
  });
  const deliveryRef = useRef(null);
  const invoiceRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setData(data);
    if (data?.order?.final_record_count) setQa((q) => ({ ...q, final_count: data.order.final_record_count }));
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="p-10">Loading…</div>;
  const { order, delivery, invoice, quality, replacements } = data;

  const upload = async (e, kind) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", kind); fd.append("parent_type", "order"); fd.append("parent_id", id);
    await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(`${kind} uploaded`); load();
  };

  const submitQA = async () => {
    await api.post(`/orders/${id}/quality`, qa);
    toast.success("QA recorded");
    load();
  };
  const changeStatus = async (status) => {
    await api.post(`/orders/${id}/status`, { status });
    toast.success("Status updated"); load();
  };
  const deliverOrder = async () => {
    await api.post(`/orders/${id}/deliver`);
    toast.success("Delivered — customer notified"); load();
  };

  return (
    <div className="App min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-xs text-muted-foreground">{order.reference}</span>
              <StatusPill status={order.status} size="lg" />
            </div>
            <h1 className="font-heading font-black tracking-tighter text-3xl">Order · {order.option_key}</h1>
            <div className="mt-3 text-sm text-muted-foreground">
              Amount <b className="text-foreground">{order.currency} {order.amount?.toLocaleString()}</b>
              {order.expected_delivery_date && <> · ETA <b className="text-foreground">{order.expected_delivery_date}</b></>}
            </div>
          </div>

          <div className="border border-border bg-white p-5">
            <div className="font-semibold mb-3">Production files</div>
            <div className="flex flex-wrap gap-2">
              <input type="file" accept=".csv,.xlsx" ref={deliveryRef} className="hidden" onChange={(e) => upload(e, "delivery")} data-testid={TID.admin.uploadDelivery} />
              <Button size="sm" variant="outline" onClick={() => deliveryRef.current?.click()} className="rounded-sm">
                <Upload size={14} className="mr-2" /> Upload draft delivery
              </Button>
              <input type="file" accept=".pdf,.xlsx,.csv" ref={invoiceRef} className="hidden" onChange={(e) => upload(e, "invoice")} />
              <Button size="sm" variant="outline" onClick={() => invoiceRef.current?.click()} className="rounded-sm">
                <Upload size={14} className="mr-2" /> Upload invoice
              </Button>
              <Button size="sm" variant="outline" onClick={() => changeStatus("quality_review")} className="rounded-sm">Submit for QA</Button>
              {order.status === "ready" && <Button size="sm" data-testid="admin-deliver-btn" onClick={deliverOrder} className="rounded-sm">Release to customer</Button>}
            </div>
            <div className="mt-4 text-xs text-muted-foreground">
              {delivery && <div>Latest delivery file: <b className="text-foreground">{delivery.original_filename}</b> (v{delivery.version})</div>}
              {invoice && <div>Invoice: <b className="text-foreground">{invoice.original_filename}</b></div>}
            </div>
          </div>

          <div className="border border-border bg-white p-5">
            <div className="font-semibold mb-3 flex items-center gap-2"><ShieldCheck size={16} weight="duotone" className="text-primary" /> Quality review</div>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              {[
                ["total_received", "Total received"], ["total_reviewed", "Total reviewed"],
                ["duplicates_removed", "Duplicates removed"], ["rejected_title_mismatch", "Rejected · title"],
                ["rejected_company_mismatch", "Rejected · company"], ["rejected_geo_mismatch", "Rejected · geo"],
                ["emails_checked", "Emails checked"], ["valid_emails", "Valid emails"],
                ["invalid_emails", "Invalid emails"], ["risky_emails", "Risky emails"],
                ["phones_checked", "Phones checked"], ["final_count", "Final approved count"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs">{l}</Label>
                  <Input type="number" value={qa[k]} onChange={(e) => setQa({ ...qa, [k]: Number(e.target.value) })} className="mt-1 h-9" />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <Label className="text-xs">Comments</Label>
              <Textarea rows={2} value={qa.comments} onChange={(e) => setQa({ ...qa, comments: e.target.value })} className="mt-1" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Select value={qa.decision} onValueChange={(v) => setQa({ ...qa, decision: v })}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approve</SelectItem>
                  <SelectItem value="rework">Return for rework</SelectItem>
                  <SelectItem value="escalated">Escalate</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid={qa.decision === "approved" ? TID.admin.qualityApprove : TID.admin.qualityReject} onClick={submitQA} className="rounded-sm">
                {qa.decision === "approved" ? <><CheckCircle size={14} className="mr-2" /> Approve for delivery</> : <><XCircle size={14} className="mr-2" /> Return for rework</>}
              </Button>
            </div>
            {quality && (
              <div className="mt-4 text-xs font-mono text-muted-foreground">
                Last review: {quality.decision} · final count {quality.final_count}
              </div>
            )}
          </div>

          {replacements?.length > 0 && (
            <div className="border border-border bg-white p-5">
              <div className="font-semibold mb-3">Replacement cases</div>
              {replacements.map((r) => (
                <div key={r.case_id} className="flex items-center justify-between border-t border-border py-2 first:border-t-0">
                  <div className="text-sm">
                    <div className="font-mono text-[11px] text-muted-foreground">{r.case_id}</div>
                    <div>{r.reason.replace(/_/g, " ")} — {r.explanation}</div>
                  </div>
                  <div className="flex gap-1">
                    {r.status === "submitted" && (
                      <>
                        <Button size="sm" onClick={async () => { await api.post(`/orders/replacements/${r.case_id}/decision`, { status: "approved" }); load(); }} className="rounded-sm h-8">Approve</Button>
                        <Button size="sm" variant="ghost" onClick={async () => { await api.post(`/orders/replacements/${r.case_id}/decision`, { status: "rejected" }); load(); }} className="rounded-sm h-8">Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <ProductionLogPanel orderId={id} />
          <FileHistoryPanel orderId={id} />
        </div>

        <aside className="space-y-6">
          <Link to={`/admin/requests/${order.request_id}`} className="block border border-border bg-white p-4 hover:border-foreground text-sm">
            View original request →
          </Link>
        </aside>
      </div>
      <Footer />
    </div>
  );
}
